export type Debounced<A extends unknown[]> = {
  (...args: A): void;
  cancel: () => void;
};

/**
 * Trailing-edge debounce: runs `fn` with the last arguments once calls have been quiet for `wait` ms.
 * With `maxWait`, a burst of calls still runs `fn` at most `maxWait` ms after its first call, like
 * lodash's `debounce(fn, wait, { maxWait })`. `cancel` drops a pending call.
 */
export const debounce = <A extends unknown[]>(
  fn: (...args: A) => unknown,
  wait: number,
  options: { maxWait?: number } = {},
): Debounced<A> => {
  const { maxWait } = options;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let burstStart: number | undefined;
  let lastArgs: A | undefined;

  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
    burstStart = undefined;
    lastArgs = undefined;
  };

  const debounced = (...args: A) => {
    const now = Date.now();
    burstStart ??= now;
    lastArgs = args;
    const remaining = maxWait === undefined ? wait : Math.min(wait, maxWait - (now - burstStart));
    clearTimeout(timer);
    timer = setTimeout(
      () => {
        const callArgs = lastArgs as A;
        cancel();
        fn(...callArgs);
      },
      Math.max(0, remaining),
    );
  };
  debounced.cancel = cancel;
  return debounced;
};
